// Per-route <head> tags for search engines and link previews. Renders nothing:
// the page itself is untouched, only <head> changes. site.json is the single
// source of truth — the prerender and SEO build scripts read the same file, so
// the tags here, the static snapshots and the sitemap cannot drift apart.
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import site from './site.json';
import { schemaFor } from './schema';

const PAGES = new Map(site.pages.map((p) => [p.path, p]));

// Trailing slashes are the same page; the canonical form has none (except "/").
export function normalizePath(pathname) {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function publicPage(pathname) {
  return PAGES.get(normalizePath(pathname)) || null;
}

// Everything this component owns carries data-seo, so a route change (or a
// client render over a prerendered snapshot) swaps the whole set rather than
// stacking duplicates. index.html's defaults carry it too.
function applyHead(title, tags) {
  document.title = title;
  document.head.querySelectorAll('[data-seo]').forEach((el) => el.remove());
  for (const [tag, attrs, text] of tags) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    if (text) el.textContent = text;
    el.setAttribute('data-seo', '');
    document.head.appendChild(el);
  }
}

function headFor(pathname) {
  const page = publicPage(pathname);
  if (!page) {
    // Auth, the signed-in app and 404s: keep today's title, stay out of indexes.
    return [site.defaultTitle, [['meta', { name: 'robots', content: 'noindex, nofollow' }]]];
  }
  const url = site.origin + page.path;
  const image = site.origin + site.ogImage.path;
  return [
    page.title,
    [
      ['meta', { name: 'description', content: page.description }],
      ['link', { rel: 'canonical', href: url }],
      ['meta', { property: 'og:type', content: 'website' }],
      ['meta', { property: 'og:site_name', content: site.siteName }],
      ['meta', { property: 'og:title', content: page.title }],
      ['meta', { property: 'og:description', content: page.description }],
      ['meta', { property: 'og:url', content: url }],
      ['meta', { property: 'og:image', content: image }],
      ['meta', { property: 'og:image:width', content: String(site.ogImage.width) }],
      ['meta', { property: 'og:image:height', content: String(site.ogImage.height) }],
      ['meta', { property: 'og:image:alt', content: site.ogImage.alt }],
      ['meta', { name: 'twitter:card', content: 'summary_large_image' }],
      ['meta', { name: 'twitter:image', content: image }],
      ['meta', { name: 'twitter:site', content: site.twitter }],
      ['meta', { name: 'twitter:title', content: page.title }],
      ['meta', { name: 'twitter:description', content: page.description }],
      ['script', { type: 'application/ld+json' }, JSON.stringify(schemaFor(page))],
    ],
  ];
}

export default function PageMeta() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const [title, tags] = headFor(pathname);
    applyHead(title, tags);
  }, [pathname]);

  // A prerendered snapshot is hidden until the app's first render replaces it,
  // so visitors see exactly what they saw before snapshots existed (see
  // scripts/prerender.mjs). Layout effect: runs after that render commits and
  // before the browser paints it.
  useLayoutEffect(() => {
    document.getElementById('prerender-hide')?.remove();
  }, []);

  return null;
}
